import { useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Mic, History, ExternalLink, Play, Camera, Download, MapPin, Globe, Euro, Heart, ChevronDown, ChevronUp, Edit, Ship, House, Star } from "lucide-react";
import LinkedinIcon from "@/components/icons/LinkedinIcon";
import { VideoEmbed } from "./VideoEmbed";
import { Button } from "@/components/ui/button";
import { SpeakerDetailsInterface } from "@/types/SpeakerDetailsInterface";
import { Link, usePage } from '@inertiajs/react'
import { formatLocation } from "@/lib/formatters";

export interface SpeakerDetailsProps {
  speaker: SpeakerDetailsInterface;
  downloadBioPDF: () => void;
  shareOnLinkedIn: () => void;
  onOpenLightbox: (index: number) => void;
  headshotOffset: number;
}

export const SpeakerDetails = ({ speaker, downloadBioPDF, shareOnLinkedIn, onOpenLightbox, headshotOffset }: SpeakerDetailsProps) => {
  const { auth } = usePage().props as { auth?: { user?: { id: number; name: string; } } };
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  const standardTalks = Array.isArray(speaker.standardTalks) ? speaker.standardTalks : [];
  const pastEvents = Array.isArray(speaker.pastEvents) ? speaker.pastEvents : [];
  const headshots = Array.isArray(speaker.headshots) ? speaker.headshots : [];
  const albumImages = Array.isArray(speaker.albumImages) ? speaker.albumImages : [];

  // albumOffset: headshots come first in the merged slide list
  const albumOffset = headshotOffset + headshots.length;

  // Todo: may need to change this if fees can be null/undefined
  const fees = speaker.fees ?? {
    paidTalks: false,
    startingPrice: undefined,
    communityTalks: false,
    travelExpensesOnly: false,
    pricingOnRequestOnly: false
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-8">
        {/* Speaker Bio and Video */}
          
          {/* Speaker Video */}
          {speaker.videoUrl && (
        <div className="space-y-0 mb-6">
            <div>
              <VideoEmbed url={speaker.videoUrl} title="Speaker Video" />
            </div>
        </div>
          )}

        {/* Available Talks */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              Contributions Offered
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Service Types */}
            <div className="mb-2">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-sm">
                  Presentations
                </Badge>
                {speaker.availableForWorkshops && (
                  <Badge variant="outline" className="text-sm">
                    Workshops
                  </Badge>
                )}
                {speaker.availableForKeynotes && (
                  <Badge variant="outline" className="text-sm">
                    Keynotes
                  </Badge>
                )}
                {speaker.availableForPanels && (
                  <Badge variant="outline" className="text-sm">
                    Panels
                  </Badge>
                )}
                {speaker.availableForExecutiveBriefings && (
                  <Badge variant="outline" className="text-sm">
                    Executive Briefings
                  </Badge>
                )}
                {speaker.availableForVirtualEvents && (
                  <Badge variant="outline" className="text-sm">
                    Virtual Events
                  </Badge>
                )}
                {speaker.availableForExercises && (
                  <Badge variant="outline" className="text-sm">
                    Exercise Facilitation
                  </Badge>
                )}
              </div>
            </div>

            {standardTalks.length > 0 && (
              <p className="text-muted-foreground mb-4 mt-6">
                Speaker has showcased the following sample talks for standard engagements, or ready for tailoring:  
              </p>
            )}
            
            <div className="space-y-4">
              {standardTalks.map((topic, index) => (
                <div key={index} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors">
                  <h4 className="font-semibold text-lg mb-2 text-primary">{topic.title}</h4>
                  <p className="text-muted-foreground leading-relaxed">{topic.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {pastEvents.length > 0 && (
        /* Past Speaking Events */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Previous Engagements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pastEvents.map((event, index) => (
                <div key={index}>
                  <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {event.eventUrl ? (
                            <a href={event.eventUrl} target="_blank" rel="noopener noreferrer" className="group">
                              <h4 className="font-semibold text-lg group-hover:text-primary transition-colors inline-flex items-center gap-1">
                                {event.name}
                                <ExternalLink className="w-4 h-4" />
                              </h4>
                            </a>
                          ) : (
                            <h4 className="font-semibold text-lg">{event.name}</h4>
                          )}
                          {event.sheSpeaksCyberHelped && (
                            <div className="inline-block relative" title="SheSpeaksCyber helped secure this engagement">
                              <Star className="w-4 h-4 text-primary fill-primary" />
                            </div>
                          )}
                        </div>
                        <h5 className="font-medium text-primary mt-1">{event.presentationTitle}</h5>
                        <p className="text-muted-foreground text-sm">{event.audience}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap sm:flex-col sm:items-end">
                        <div className="flex gap-2">
                          <Badge variant="outline">{event.type}</Badge>
                          <Badge variant="secondary">{event.year}</Badge>
                        </div>
                        {event.videoUrl && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="mt-2.5 flex items-center gap-1">
                                <Play className="w-3 h-3" />
                                Watch Recording
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl">
                              <DialogHeader>
                                <DialogTitle>{event.presentationTitle}</DialogTitle>
                              </DialogHeader>
                              <div className="mt-4">
                                <VideoEmbed url={event.videoUrl} title={event.presentationTitle} />
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </div>
                  </div>
                  {index < speaker.pastEvents.length - 1 && <Separator className="mt-4" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Album Carousel for non-high-quality images */}
        {albumImages && albumImages.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Speaker Album
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Carousel>
                <CarouselContent>
                  {albumImages.map((img, idx) => (
                    <CarouselItem key={img.id} className="basis-1/2 sm:basis-1/3">
                      <div
                        className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer"
                        onClick={() => onOpenLightbox(albumOffset + idx)}
                      >
                        <img
                          src={img.thumbnailUrl}
                          alt={img.caption}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="-left-10" />
                <CarouselNext className="-right-10" />
              </Carousel>
            </CardContent>
          </Card>
        )}

        {/* CTA – shown when profile has no video, talks, or past events */}
        {!speaker.videoUrl && standardTalks.length === 0 && pastEvents.length === 0 && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-8 space-y-4">
            <h3 className="text-lg font-semibold">Get more from SheSpeaksCyber</h3>
            <p className="text-muted-foreground mb-5">
              Discover more ways to get involved — whether you're looking to speak, find opportunities, or just get inspired.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link href="/signup">Join SheSpeaksCyber</Link>
              </Button>
              <Button asChild variant="outline">
                <Link href="/events">Browse Calls for Speakers</Link>
              </Button>
              <Button asChild variant="outline">
                <Link href="/insights">Learn from Our Insights</Link>
              </Button>
            </div>
          </div>
        )}

      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Location and Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Location & Availability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Based in</h4>
              <div className="flex items-center gap-2">
                <House className="w-4 h-4 text-muted-foreground" />
                <p className="text-muted-foreground">{formatLocation(speaker.hometown, speaker.country)}</p>
              </div>
            </div>
            
            <Separator />
            
            <div>
              <h4 className="font-medium mb-2">Travel Availability</h4>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {speaker.travelAvailability === "local" && "Local engagements only"}
                  {speaker.travelAvailability === "national" && "National speaking engagements"}
                  {speaker.travelAvailability === "regional" && "Regional travel (same continent)"}
                  {speaker.travelAvailability === "global" && "Available for engagements worldwide"}
                </span>
              </div>
            </div>
            
            
            {(fees.travelExpensesOnly || fees.communityTalks || fees.paidTalks) && (
            <div>
              <Separator />
              <h4 className="font-medium mt-4 mb-2">Speaking Fees</h4>
              <div className="space-y-2">
                {fees.paidTalks && (
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {fees.startingPrice ? `Paid engagements (from €${fees.startingPrice.toLocaleString()})` : 'Paid engagements (pricing on request)'}
                    </span>
                  </div>
                )}
                {fees.communityTalks && (
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Community and non-profit events</span>
                  </div>
                )}
                {fees.travelExpensesOnly && (
                <div className="flex items-center gap-2">
                  <Ship className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Travel and expenses coverage required</span>
                </div>
                )}
              </div>
            </div>
            )}
            
            {speaker.languages.length > 0 && (
            <div>
              <Separator />
              <h4 className="font-medium mt-4 mb-2">Languages</h4>
              <div className="flex flex-wrap gap-2">
                {(speaker.languages ?? []).map((lang, index) => (
                  <Badge key={index} variant="outline" className="text-sm">
                    {lang}
                  </Badge>
                ))}
              </div>
            </div>
            )}
          </CardContent>
        </Card>

        {/* Speaker Photos */}
        {headshots && headshots.length > 0 && (
          <Card data-testid="headshots-section">
            <CardHeader className="pb-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Marketing Photos
              </CardTitle>
              <p className="text-sm text-muted-foreground mb-4">
                Available for use by events featuring this speaker.
              </p>
            </CardHeader>
            <CardContent>
              <div className={`space-y-4 overflow-hidden transition-all duration-300 ${
                showAllPhotos ? 'max-h-none' : 'max-h-96'
              }`}>
                {headshots.map((headshot, index) => (
                  <div key={index} className="group relative">
                    <div
                      className="aspect-[4/5] rounded-lg overflow-hidden bg-muted cursor-pointer"
                      onClick={() => onOpenLightbox(headshotOffset + index)}
                    >
                      <img
                        src={headshot.thumbnailUrl}
                        alt={headshot.caption}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="mt-2 space-y-2">
                      <p className="text-sm font-medium">{headshot.caption}</p>
                      <div className="flex items-center justify-between">
                        <Button variant="outline" size="sm" asChild>
                          <a href={headshot.imageUrl} className="flex items-center gap-1" target="_blank">
                            <Download className="w-3 h-3" />
                            Download
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {headshots.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAllPhotos(!showAllPhotos)}
                  className="w-full mt-4"
                >
                  {showAllPhotos ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Collapse to Hide
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Expand to Download
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={shareOnLinkedIn}
            className="w-full"
          >
            <LinkedinIcon className="w-4 h-4 mr-1" />
            Share on LinkedIn
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className={auth?.user?.id === speaker.id ? "flex-1" : "w-full"} onClick={downloadBioPDF}>
              <Download className="w-4 h-4 mr-1" />
              Download as PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};